import React, { useState } from 'react';
import { Row, Col, Card, Table, Button, Badge, Form, InputGroup, Alert } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FaPlus, FaEdit, FaTrash, FaUpload, FaSearch } from 'react-icons/fa';
import { agentService, groupeService } from '../services/api';
import AgentModal from '../components/AgentModal';
import PhotoUploadModal from '../components/PhotoUploadModal';
import { getStaticFileUrl } from '../config/api';

const Agents = () => {
  const [showModal, setShowModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [filters, setFilters] = useState({
    actif: true
  });

  const queryClient = useQueryClient();

  // Queries
  const { data: agents = [], isLoading, error } = useQuery(
    ['agents', filters], 
    () => agentService.getAll(filters).then(res => res.data),
    { keepPreviousData: true }
  );

  const { data: groupes = [] } = useQuery('groupes', () => 
    groupeService.getAll().then(res => res.data)
  );

  const { data: fonctions = [] } = useQuery('fonctions', () => 
    agentService.getFonctions().then(res => res.data)
  );

  // Mutations
  const deleteMutation = useMutation(agentService.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('agents');
    }
  });

  const handleEdit = (agent) => {
    setSelectedAgent(agent);
    setShowModal(true);
  };

  const handleDelete = async (agent) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${agent.nom} ${agent.prenoms} ?`)) {
      try {
        await deleteMutation.mutateAsync(agent.id);
      } catch (error) {
        alert('Erreur lors de la suppression: ' + error.message);
      }
    }
  };

  const handlePhotoUpload = (agent) => {
    setSelectedAgent(agent);
    setShowPhotoModal(true);
  };

  const handleAddNew = () => {
    setSelectedAgent(null);
    setShowModal(true);
  };

  const handleFilterChange = (field, value) => {
    // Pour les champs vides, ne pas les inclure dans les filtres
    if (value === '' || value === null || value === undefined) {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      });
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const getGroupeBadge = (agent) => {
    if (!agent.groupeId) return <Badge bg="secondary">Sans groupe</Badge>;
    
    // Trouver le groupe complet à partir de son ID
    const groupe = groupes.find(g => g.id === agent.groupeId);
    if (!groupe) return <Badge bg="secondary">Groupe introuvable</Badge>;
    
    return (
      <Badge bg="primary" style={{ backgroundColor: groupe.couleurHex || '#0d6efd' }}>
        {groupe.nom}
      </Badge>
    );
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    // Extraire le nom du fichier du chemin
    const filename = photoPath.split('/').pop();
    return getStaticFileUrl(filename);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Gestion des Agents</h2>
          <p className="text-muted">Gérez les informations et photos des agents</p>
        </div>
        <Button variant="primary" onClick={handleAddNew}>
          <FaPlus className="me-2" />
          Nouvel Agent
        </Button>
      </div>

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Recherche</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Nom ou prénoms..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Groupe</Form.Label>
                <Form.Select
                  value={filters.groupeId}
                  onChange={(e) => handleFilterChange('groupeId', e.target.value)}
                >
                  <option value="">Tous les groupes</option>
                  {groupes.map(groupe => (
                    <option key={groupe.id} value={groupe.id}>
                      {groupe.nom}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Fonction</Form.Label>
                <Form.Select
                  value={filters.fonction}
                  onChange={(e) => handleFilterChange('fonction', e.target.value)}
                >
                  <option value="">Toutes les fonctions</option>
                  {fonctions.map(fonction => (
                    <option key={fonction} value={fonction}>
                      {fonction}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filters.actif}
                  onChange={(e) => handleFilterChange('actif', e.target.value === 'true')}
                >
                  <option value={true}>Actifs seulement</option>
                  <option value={false}>Inactifs seulement</option>
                  <option value="">Tous</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Messages d'erreur */}
      {error && (
        <Alert variant="danger">
          Erreur lors du chargement des agents: {error.message}
        </Alert>
      )}

      {/* Tableau des agents */}
      <Card>
        <Card.Body>
          {isLoading ? (
            <div className="text-center p-4">Chargement...</div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Nom Complet</th>
                  <th>Matricule</th>
                  <th>Fonction</th>
                  <th>Groupe</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id}>
                    <td>
                      {agent.photoPath ? (
                        <img
                          src={getPhotoUrl(agent.photoPath)}
                          alt={`${agent.nom} ${agent.prenoms}`}
                          style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%', 
                            backgroundColor: '#dee2e6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                        >
                          Pas de photo
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{agent.nom} {agent.prenoms}</strong>
                    </td>
                    <td>{agent.matricule || 'N/A'}</td>
                    <td>{agent.fonction || 'N/A'}</td>
                    <td>{getGroupeBadge(agent)}</td>
                    <td>
                      <Badge bg={agent.actif ? 'success' : 'secondary'}>
                        {agent.actif ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(agent)}
                          title="Modifier"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => handlePhotoUpload(agent)}
                          title="Gérer la photo"
                        >
                          <FaUpload />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(agent)}
                          title="Supprimer"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {agents.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center p-4">
                      Aucun agent trouvé avec ces critères
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modales */}
      <AgentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        agent={selectedAgent}
        groupes={groupes}
      />

      <PhotoUploadModal
        show={showPhotoModal}
        onHide={() => setShowPhotoModal(false)}
        agent={selectedAgent}
      />
    </div>
  );
};

export default Agents; 